/**
 * Original decorative artwork, drawn as SVG.
 *
 * Everything here is authored for this project, so it ships with the app, adds
 * no network weight, scales cleanly, and carries no licensing.
 */

/** Landing-page scene: Planet Nine, a moon, and a ship on approach. */
export function HeroScene() {
  return (
    <svg
      className="hero-art"
      viewBox="0 0 320 220"
      role="img"
      aria-label="A ship approaching Planet Nine, a banded blue ice giant"
    >
      <defs>
        <radialGradient id="hero-planet" cx="34%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#d6efff" />
          <stop offset="32%" stopColor="#5cb6ff" />
          <stop offset="70%" stopColor="#1d4ea8" />
          <stop offset="100%" stopColor="#0a1c44" />
        </radialGradient>
        <linearGradient id="hero-shadow" x1="0" y1="0" x2="1" y2="0.35">
          <stop offset="42%" stopColor="#05070f" stopOpacity="0" />
          <stop offset="100%" stopColor="#05070f" stopOpacity="0.8" />
        </linearGradient>
        <radialGradient id="hero-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#4aa6ff" stopOpacity="0.42" />
          <stop offset="60%" stopColor="#4aa6ff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#4aa6ff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="hero-ring" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4fd8ff" stopOpacity="0.06" />
          <stop offset="48%" stopColor="#9ee4ff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#4aa6ff" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="hero-comet" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#dff1ff" stopOpacity="0" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="hero-hull" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eef3ff" />
          <stop offset="100%" stopColor="#8ea3cc" />
        </linearGradient>
        <linearGradient id="hero-flame" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4fd8ff" />
          <stop offset="100%" stopColor="#8b7dff" stopOpacity="0" />
        </linearGradient>
        <clipPath id="hero-planet-clip">
          <circle cx="212" cy="112" r="62" />
        </clipPath>
      </defs>

      <circle className="hero-glow" cx="212" cy="112" r="98" fill="url(#hero-glow)" />

      <g className="hero-stars">
        <circle cx="24" cy="30" r="1.6" fill="#fff" />
        <circle cx="286" cy="42" r="1.2" fill="#cfe0ff" />
        <circle cx="60" cy="176" r="1.4" fill="#fff" />
        <circle cx="252" cy="186" r="1" fill="#b9ccff" />
        <circle cx="152" cy="18" r="1.1" fill="#fff" />
        <circle cx="300" cy="130" r="1.5" fill="#dff1ff" />
        <circle cx="12" cy="112" r="1.2" fill="#fff" />
        <circle cx="130" cy="200" r="1.1" fill="#cfe0ff" />
        <circle cx="196" cy="206" r="0.9" fill="#fff" />
      </g>

      <g className="hero-comet-group">
        <rect x="-46" y="-1.4" width="46" height="2.8" rx="1.4" fill="url(#hero-comet)" />
        <circle cx="0" cy="0" r="2.6" fill="#fff" />
      </g>

      <circle cx="212" cy="112" r="62" fill="url(#hero-planet)" />
      <g clipPath="url(#hero-planet-clip)">
        <ellipse cx="212" cy="84" rx="72" ry="6.5" fill="#a9dcff" opacity="0.26" />
        <ellipse cx="212" cy="100" rx="72" ry="4.5" fill="#0d2f6b" opacity="0.4" />
        <ellipse cx="212" cy="116" rx="72" ry="8.5" fill="#bde6ff" opacity="0.16" />
        <ellipse cx="212" cy="133" rx="72" ry="5.5" fill="#0d2f6b" opacity="0.46" />
        <ellipse cx="212" cy="148" rx="72" ry="7" fill="#a9dcff" opacity="0.12" />
        <ellipse className="hero-storm" cx="188" cy="126" rx="15" ry="7.5" fill="#e6f5ff" opacity="0.3" />
      </g>
      <circle cx="212" cy="112" r="62" fill="url(#hero-shadow)" />

      <ellipse
        className="hero-ring"
        cx="212"
        cy="112"
        rx="104"
        ry="22"
        fill="none"
        stroke="url(#hero-ring)"
        strokeWidth="7"
      />

      <circle cx="86" cy="52" r="15" fill="#2b3860" />
      <circle cx="81" cy="48" r="4" fill="#1a2440" />
      <circle cx="92" cy="58" r="2.6" fill="#1a2440" />

      <g className="hero-ship">
        <path d="M62 150l30-9 30 9-30 12z" fill="#3f5290" opacity="0.55" />
        <path d="M92 118c9 6 15 17 15 29l-15 8-15-8c0-12 6-23 15-29Z" fill="url(#hero-hull)" />
        <circle cx="92" cy="140" r="6" fill="#0a1020" />
        <circle cx="92" cy="140" r="3.4" fill="#4fd8ff" />
        <path d="M77 147l-14 8 14 0z" fill="#8b7dff" />
        <path d="M107 147l14 8-14 0z" fill="#8b7dff" />
        <path d="M84 155h16l-8 26z" fill="url(#hero-flame)" className="hero-flame" />
      </g>
    </svg>
  );
}

/** Console deck under the trick: orbit rings, rim ticks and a faint rocket. */
export function TableDeck() {
  const ticks = Array.from({ length: 36 }, (_, i) => i);
  return (
    <svg className="arena__deck" viewBox="0 0 800 460" aria-hidden="true" focusable="false">
      <g stroke="#4fd8ff" fill="none">
        <ellipse cx="400" cy="230" rx="330" ry="180" strokeOpacity="0.2" strokeWidth="2" />
        <ellipse
          cx="400"
          cy="230"
          rx="250"
          ry="132"
          strokeOpacity="0.14"
          strokeWidth="1.5"
          strokeDasharray="6 12"
        />
        <ellipse cx="400" cy="230" rx="152" ry="80" strokeOpacity="0.1" strokeWidth="1.5" />
      </g>
      <g>
        {ticks.map((i) => {
          const a = (i / ticks.length) * Math.PI * 2;
          const major = i % 3 === 0;
          return (
            <line
              key={i}
              x1={400 + 338 * Math.cos(a)}
              y1={230 + 187 * Math.sin(a)}
              x2={400 + (major ? 312 : 324) * Math.cos(a)}
              y2={230 + (major ? 172 : 179) * Math.sin(a)}
              stroke="#4fd8ff"
              strokeOpacity={major ? 0.5 : 0.22}
              strokeWidth={major ? 2.4 : 1.4}
            />
          );
        })}
      </g>
    </svg>
  );
}

/** Slow background traffic so the void is never completely still. */
export function AmbientDrifters() {
  return (
    <div className="drifters" aria-hidden="true">
      <svg className="drifter drifter--ufo" viewBox="0 0 120 74" focusable="false">
        <ellipse cx="60" cy="52" rx="46" ry="6" fill="#4fd8ff" opacity="0.18" />
        <path d="M14 40h92c0 9-21 15-46 15S14 49 14 40Z" fill="#7f93c9" />
        <ellipse cx="60" cy="40" rx="46" ry="11" fill="#aebde6" />
        <path d="M38 34c4-12 12-19 22-19s18 7 22 19Z" fill="#9ee4ff" opacity="0.95" />
        <circle cx="30" cy="43" r="3" fill="#ffd166" />
        <circle cx="60" cy="45" r="3" fill="#ff5fa8" />
        <circle cx="90" cy="43" r="3" fill="#35e0a1" />
      </svg>

      <svg className="drifter drifter--alien" viewBox="0 0 64 76" focusable="false">
        <ellipse cx="32" cy="34" rx="22" ry="26" fill="#7ee08a" />
        <ellipse cx="23" cy="30" rx="6" ry="9" fill="#0a1020" transform="rotate(-16 23 30)" />
        <ellipse cx="41" cy="30" rx="6" ry="9" fill="#0a1020" transform="rotate(16 41 30)" />
        <path d="M25 46q7 6 14 0" stroke="#0a1020" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M20 12 14 2M44 12 50 2" stroke="#7ee08a" strokeWidth="3" strokeLinecap="round" />
        <circle cx="14" cy="2" r="3" fill="#ffd166" />
        <circle cx="50" cy="2" r="3" fill="#ffd166" />
        <path d="M18 58h28l-6 16H24z" fill="#5cb6ff" />
      </svg>

      <svg className="drifter drifter--sat" viewBox="0 0 110 56" focusable="false">
        <rect x="44" y="18" width="22" height="20" rx="4" fill="#c9d6f5" />
        <rect x="6" y="20" width="32" height="16" rx="2" fill="#3f5290" />
        <rect x="72" y="20" width="32" height="16" rx="2" fill="#3f5290" />
        <path d="M38 28h6M66 28h6" stroke="#8ea3cc" strokeWidth="3" />
        <circle cx="55" cy="12" r="4" fill="#4fd8ff" />
      </svg>

      <svg className="drifter drifter--toast" viewBox="0 0 60 60" focusable="false">
        <rect x="8" y="14" width="44" height="38" rx="7" fill="#e8b872" />
        <rect x="14" y="20" width="32" height="26" rx="4" fill="#c98f45" opacity="0.55" />
        <path d="M18 6c0 6 6 6 6 12M34 4c0 7 6 6 6 13" stroke="#ffd166" strokeWidth="3" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export type MissionKind = 'delivery' | 'order' | 'comms' | 'command' | 'precision';
/** Small emblem shown on each mission row so the list scans visually. */
export function MissionIcon({ kind }: { kind: MissionKind }) {
  const common = { viewBox: '0 0 24 24', className: 'mission-icon', 'aria-hidden': true } as const;
  switch (kind) {
    case 'order':
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h11M4 18h6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      );
    case 'comms':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" fill="currentColor" />
          <path d="M6.5 6.5a8 8 0 0 0 0 11M17.5 6.5a8 8 0 0 1 0 11" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </svg>
      );
    case 'command':
      return (
        <svg {...common}>
          <path d="M12 2l2.6 6.2 6.7.5-5.1 4.4 1.6 6.5L12 16.1 6.2 19.6l1.6-6.5L2.7 8.7l6.7-.5z" fill="currentColor" />
        </svg>
      );
    case 'precision':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
          <path d="M12 4v16M4 8.5l8 4.5 8-4.5" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.7" />
        </svg>
      );
  }
}

interface MissionShape {
  ordered?: number | 'all';
  finalTask?: boolean;
  rules?: {
    noCommunication?: boolean;
    communicationLockedUntilTrick?: number;
    communicationClosesAtTrick?: number;
    commanderWinsFirstTrick?: boolean;
    commanderWinsNoTricks?: boolean;
    noBackToBackTricks?: boolean;
    everyoneWinsATrick?: boolean;
    maxTricksPerPlayer?: number;
  };
}

/** Derives an emblem from what the mission actually asks of the crew. */
export function missionKind(mission: MissionShape): MissionKind {
  const rules = mission.rules ?? {};
  if (
    rules.noCommunication ||
    rules.communicationLockedUntilTrick ||
    rules.communicationClosesAtTrick
  ) {
    return 'comms';
  }
  if (rules.commanderWinsFirstTrick || rules.commanderWinsNoTricks) return 'command';
  if (rules.maxTricksPerPlayer || rules.everyoneWinsATrick || rules.noBackToBackTricks) {
    return 'precision';
  }
  if (mission.ordered || mission.finalTask) return 'order';
  return 'delivery';
}
